import { MultipleChoiceBlockV2 } from "#src/commonV2/inputs/MultipleChoiceBlock";
import { RadioV2 } from "#src/commonV2/inputs/Radio";
import { SectionCard } from "#src/commonV2/SectionCard";
import { PaymentMethodValue } from "#src/services/payment/enum";
import { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as VietcombankSquare } from "#src/assets/images/logos/banks/vietcombank-square.svg";
import { ReactComponent as VPBankSquare } from "#src/assets/images/logos/banks/vpbank-square.svg";
import { ReactComponent as TechcombankSquare } from "#src/assets/images/logos/banks/techcombank-square.svg";
import { ReactComponent as ACBSquare } from "#src/assets/images/logos/banks/acb-square.svg";
import { ReactComponent as VIBSquare } from "#src/assets/images/logos/banks/vib-square.svg";
import { ReactComponent as MBBankSquare } from "#src/assets/images/logos/banks/mbbank-square.svg";
import { ReactComponent as VisaSvg } from "#src/assets/images/logos/banks/visa.svg";
import { ReactComponent as MasterCardSvg } from "#src/assets/images/logos/banks/mastercard.svg";
import { ReactComponent as AmericanExpressSvg } from "#src/assets/images/logos/banks/americanexpress.svg";
import { Tooltip } from "@lockerpm/design";
import { TAILWIND_COLORS } from "#src/utils/common";

interface IPaymentEnterInformationPaymentMethodProps {
  paymentMethod: PaymentMethodValue;
  setPaymentMethod: Dispatch<SetStateAction<PaymentMethodValue>>;
}

export const PaymentEnterInformationPaymentMethod = ({
  paymentMethod,
  setPaymentMethod,
}: IPaymentEnterInformationPaymentMethodProps) => {
  const { t } = useTranslation("common", {
    keyPrefix: "v2.payment.enterInformation.paymentMethod",
  });

  // --------------- METHODS ---------------

  // --------------- RENDER ---------------

  return (
    <SectionCard header={t("title")}>
      <div className="p-4 grid grid-cols-2 gap-x-6 gap-y-4 rounded-xl bg-white border border-gray-v2-200">
        {/* Banking method */}
        <MultipleChoiceBlockV2
          selected={paymentMethod === PaymentMethodValue.BANKING}
          onSelect={() => {
            setPaymentMethod(PaymentMethodValue.BANKING);
          }}
          className="flex-col items-start"
        >
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-4">
              <RadioV2 checked={paymentMethod === PaymentMethodValue.BANKING} />
              <span className="text-lg">{t("banking.title")}</span>
            </div>
            <span className="pl-9 font-normal text-gray-v2-600">
              {t("banking.note")}
            </span>
          </div>
          <div className="flex items-center justify-between gap-4 w-full">
            <div className="flex items-center gap-3">
              <VietcombankSquare className="h-7 w-7 min-w-7" />
              <VPBankSquare className="h-7 w-7 min-w-7" />
              <TechcombankSquare className="h-7 w-7 min-w-7" />
              <ACBSquare className="h-7 w-7 min-w-7" />
              <VIBSquare className="h-7 w-7 min-w-7" />
              <MBBankSquare className="h-7 w-7 min-w-7" />
            </div>
            <a
              href={"https://api.vietqr.vn/vi/danh-sach-ma-ngan-hang"}
              target="_blank"
              rel="noreferrer"
              className="px-0.5 no-underline text-sm text-brand-v2-700 hover:text-brand-v2-800"
            >
              {t("banking.seeAll")}
            </a>
          </div>
        </MultipleChoiceBlockV2>
        {/* Banking method end */}

        {/* Card method */}
        <Tooltip
          color={TAILWIND_COLORS["gray-v2-900"]}
          title={
            // TODO: Remove this when we complete the international payment flow.
            <span className="text-xs text-white font-semibold text-center align-center">
              {t("tooltip.notAvailable")}
            </span>
          }
        >
          {/* We need the useless div here so that Antd Tooltip can work. */}
          <div>
            <MultipleChoiceBlockV2
              selected={paymentMethod === PaymentMethodValue.CARD}
              onSelect={() => {
                setPaymentMethod(PaymentMethodValue.CARD);
              }}
              className="flex-col items-start"
              disabled
            >
              <div className="flex flex-col gap-0.5">
                <div className="flex items-center gap-4">
                  <RadioV2
                    checked={paymentMethod === PaymentMethodValue.CARD}
                    disabled
                  />
                  <span className="text-lg">{t("card.title")}</span>
                </div>
                <span className="pl-9 font-normal text-gray-v2-600">
                  {t("card.note")}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <VisaSvg className="h-7 w-7 min-w-7" />
                <MasterCardSvg className="h-7 w-7 min-w-7" />
                <AmericanExpressSvg className="h-7 w-7 min-w-7" />
              </div>
            </MultipleChoiceBlockV2>
          </div>
        </Tooltip>
        {/* Card method end */}

        <span className="px-4 py-3 col-span-2 rounded-xl text-md font-semibold border border-warning-v2-100 bg-warning-v2-25 text-warning-v2">
          {t("noteToRequestVat")}
        </span>
      </div>
    </SectionCard>
  );
};
